import type { JobLocationUpdate, JobOrderUpdate, RowKey, Shift } from "@domain/CRM/shift";
import {
	buildShiftJobUpdates,
	computeJobDropoffLinks,
	computeMixedOrderUpdates,
	computeStopMembershipPlan,
	interleaveStopRowKeys,
	jobRowKey,
	newStopRowKey,
	parseRowKey,
} from "@domain/CRM/shift";
import type { MapFeatureEdit, PullInZone } from "@domain/CRM/job";
import { buildMapFeatureEdits } from "@domain/CRM/job";
import type { IShifts } from "@ports/shifts/IShifts";
import type { IShiftDropoffs } from "@ports/shifts/IShiftDropoffs";
import type { IJobs } from "@ports/jobs/IJobs";

export interface ShiftsUseCasesDeps {
	shiftsApi: IShifts;
	/** Junction persistence for dropoff stops (create/re-link/delete on save). */
	stopsApi: IShiftDropoffs;
	/** `setOrders` persists the run-sequence projection (`Jobs.Order`); `setDropoffs` the
	 *  sample-routing link (`Jobs."Dropoffs/Shipping"`); `setLocations` the cluster pull-in
	 *  point (`Jobs.Location`); `applyMapFeatureEdits` the pull-in zone deltas
	 *  (`Jobs."Map Features GeoJSON (code)"`). */
	jobsApi: Pick<IJobs, "setOrders" | "setDropoffs" | "setLocations" | "applyMapFeatureEdits">;
}

/** One batch of staged scheduling edits to persist. */
export interface SaveScheduleChangesInput {
	/** Brand-new shifts to create (already mapped from the UI's pending drafts to Shift fields). */
	creates: Partial<Shift>[];
	/** The full set of existing shifts; used to resolve which ones the staged maps touch. */
	existingShifts: Shift[];
	/** shiftId → jobIds to add. */
	additions: ReadonlyMap<string, ReadonlySet<string>>;
	/** shiftId → jobIds to remove. */
	removals: ReadonlyMap<string, ReadonlySet<string>>;
	/** shiftId → intended ordered jobIds for touched existing shifts (selection-sequence/anchor
	 *  inserts and row reorders). Reconciled against merged membership; a shift present only here
	 *  is a pure reorder. */
	orderedJobIdsByShiftId: ReadonlyMap<string, readonly string[]>;
	/** Current `Jobs.Order` for every loaded shift/backlog job; drives the minimal-write diff. */
	knownOrderByJobId: ReadonlyMap<string, number | undefined>;

	// ── Dropoff stops (all optional — a stop-free save is exactly the old behavior) ──────────
	/** shiftId → committed junction ids, from the loaded stops. */
	committedStopsByShiftId?: ReadonlyMap<string, readonly string[]>;
	/** shiftId → staged MIXED sequence (row keys `job:`/`stop:`/`newstop:`). May still contain
	 *  removal-staged rows (struck-through in the UI); they're filtered here. Where present it
	 *  is the ordering authority for that shift. */
	orderedRowKeysByShiftId?: ReadonlyMap<string, readonly RowKey[]>;
	/** Staged new stops' metadata by temp key. */
	newStopsByKey?: ReadonlyMap<string, { dropoffId: string }>;
	/** Committed stops staged for deletion (the portal deletes; MC orphans). */
	removedStopIds?: ReadonlySet<string>;
	/** Per pending-create index: trailing new-stop temp keys (drafts append stops after jobs). */
	createStopKeysByIndex?: ReadonlyMap<number, readonly string[]>;
	/** Current `ShiftDropoffs.Order` per committed stop; minimal-write diff. */
	knownStopOrderByStopId?: ReadonlyMap<string, number | undefined>;
	/** Current `Jobs."Dropoffs/Shipping"` head per job; minimal-write diff for the tagging. */
	knownDropoffLinkByJobId?: ReadonlyMap<string, string | undefined>;

	// ── Cluster pull-in points (both optional — a location-free save is exactly the old behavior) ──
	/** jobId → the `"lat,lng"` point staged for it. Every job in one cluster shares the string. */
	stagedLocationByJobId?: ReadonlyMap<string, string>;
	/** Current `Jobs.Location` per job; drives the minimal-write diff, like `knownOrderByJobId`. */
	knownLocationByJobId?: ReadonlyMap<string, string | undefined>;

	// ── Pull-in zones (both optional — a zone-free save is exactly the old behavior) ─────────
	/** jobId → the zones drawn for it this session. Sent as DELTAS: the endpoint merges them
	 *  into the job's current column, so there is no baseline to diff against here. */
	stagedPullInZonesByJobId?: ReadonlyMap<string, readonly PullInZone[]>;
	/** Jobs whose committed pull-in zones are cleared on this save (before any staged zones of
	 *  theirs are appended — clear-then-draw is a replace). */
	clearedPullInZoneJobIds?: ReadonlySet<string>;
}

export interface SaveScheduleChangesResult {
	/** The `Jobs.Order` writes actually persisted. The caller must fold these into whatever it
	 *  feeds back as `knownOrderByJobId` — job refetches keyed on the id *set* won't observe a
	 *  pure reorder, so the baseline would otherwise go stale and mis-diff the next save. */
	orderUpdates: JobOrderUpdate[];
	/** The `Jobs.Location` writes actually persisted. Fold these into whatever the caller feeds
	 *  back as `knownLocationByJobId` — for the same reason as `orderUpdates`: a job refetch keyed
	 *  on the id *set* won't observe a location-only change, so the baseline would go stale and
	 *  mis-diff the next save. */
	locationUpdates: JobLocationUpdate[];
	/** The pull-in zone edits sent. Informational: nothing to fold back — the post-save reconcile
	 *  re-reads the jobs, which is when the merged column shows up. */
	mapFeatureEdits: MapFeatureEdit[];
}

/**
 * Write path for the scheduling board. Encapsulates the "save staged shift edits" orchestration
 * so VMs depend on this use case rather than `API.Data.Shifts` + the merge rule inline. The
 * `(current ∪ additions) \ removals` math lives in the `@domain/CRM/shift` `buildShiftJobUpdates`
 * operation; the mixed-sequence stop planning lives in `stopPlanning`; this use case sequences
 * the create/update/delete calls through the ports.
 */
export interface ShiftsUseCases {
	/** Create the pending shifts and update every existing shift whose job set changed. Each call
	 *  no-ops the half (create / update) it has nothing for. */
	saveScheduleChanges(input: SaveScheduleChangesInput): Promise<SaveScheduleChangesResult>;
}

const EMPTY_ROW_KEYS: ReadonlyMap<string, readonly RowKey[]> = new Map();
const EMPTY_STOPS: ReadonlyMap<string, readonly string[]> = new Map();
const EMPTY_META: ReadonlyMap<string, { dropoffId: string }> = new Map();
const EMPTY_IDS: ReadonlySet<string> = new Set();
const EMPTY_ORDERS: ReadonlyMap<string, number | undefined> = new Map();
const EMPTY_LOCATIONS: ReadonlyMap<string, string> = new Map();
const EMPTY_KNOWN_LOCATIONS: ReadonlyMap<string, string | undefined> = new Map();
const EMPTY_ZONES: ReadonlyMap<string, readonly PullInZone[]> = new Map();

export async function saveScheduleChanges(
	deps: ShiftsUseCasesDeps,
	input: SaveScheduleChangesInput,
): Promise<SaveScheduleChangesResult> {
	const stagedRowKeys = input.orderedRowKeysByShiftId ?? EMPTY_ROW_KEYS;
	const committedStops = input.committedStopsByShiftId ?? EMPTY_STOPS;
	const newStopsByKey = input.newStopsByKey ?? EMPTY_META;
	const removedStopIds = input.removedStopIds ?? EMPTY_IDS;
	const createStopKeys = input.createStopKeysByIndex ?? new Map<number, readonly string[]>();

	const updates = buildShiftJobUpdates(
		input.existingShifts,
		input.additions,
		input.removals,
		input.orderedJobIdsByShiftId,
	);

	// ── Final mixed sequence per shift ─────────────────────────────────────────────────────────
	// The staged row-key list is the ordering authority; rows that didn't survive the save are
	// dropped here: job keys outside the shift's final membership (removal-staged / moved away)
	// and removal-staged stop keys. Shifts without stops fall back to their jobs-only list, so a
	// stop-free save reduces to the old computation exactly.
	const finalJobIdsByShiftId = new Map<string, readonly string[]>();
	for (const update of updates) finalJobIdsByShiftId.set(update.id, update.jobIds);
	const finalRowKeysByShiftId = new Map<string, RowKey[]>();
	const touchedShiftIds = new Set<string>([...stagedRowKeys.keys(), ...finalJobIdsByShiftId.keys()]);
	for (const shiftId of touchedShiftIds) {
		const membership =
			finalJobIdsByShiftId.get(shiftId) ?? input.existingShifts.find((s) => s.id === shiftId)?.jobIds ?? [];
		const memberSet = new Set(membership);
		const staged = stagedRowKeys.get(shiftId);
		if (!staged) {
			// No staged mixed order (the card was never opened) — but the shift may still carry
			// committed stops. Reconstruct its committed sequence so their slots survive; a
			// jobs-only fallback here would drop the stop and collide a job onto its Order. Any
			// removal-staged stop is excluded (it's being deleted). Stop-free shifts reduce to
			// the plain jobs list exactly as before.
			const committedStopIds = (committedStops.get(shiftId) ?? []).filter((id) => !removedStopIds.has(id));
			const stopsForOrder = committedStopIds.map((id) => ({
				id,
				order: input.knownStopOrderByStopId?.get(id),
			}));
			finalRowKeysByShiftId.set(shiftId, interleaveStopRowKeys(membership, input.knownOrderByJobId, stopsForOrder));
			continue;
		}
		const kept: RowKey[] = [];
		const placedJobs = new Set<string>();
		for (const key of staged) {
			const parsed = parseRowKey(key);
			if (parsed.kind === "job") {
				if (!memberSet.has(parsed.id) || placedJobs.has(parsed.id)) continue;
				placedJobs.add(parsed.id);
				kept.push(jobRowKey(parsed.id));
			} else if (parsed.kind === "stop") {
				if (!removedStopIds.has(parsed.id)) kept.push(key);
			} else {
				kept.push(key);
			}
		}
		for (const id of membership) if (!placedJobs.has(id)) kept.push(jobRowKey(id));
		finalRowKeysByShiftId.set(shiftId, kept);
	}

	// Pending drafts: jobs in draft order, then the draft's trailing stops.
	const createRowKeyLists: RowKey[][] = input.creates.map((create, index) => [
		...(create.jobIds ?? []).map(jobRowKey),
		...(createStopKeys.get(index) ?? []).map(newStopRowKey),
	]);

	// ── Order + membership plans (pure) ────────────────────────────────────────────────────────
	const netRemoved = new Set<string>();
	for (const set of input.removals.values()) for (const id of set) netRemoved.add(id);
	const { jobOrders, stopOrders, newStopOrders } = computeMixedOrderUpdates(
		[...createRowKeyLists, ...finalRowKeysByShiftId.values()],
		netRemoved,
		input.knownOrderByJobId,
		input.knownStopOrderByStopId ?? EMPTY_ORDERS,
	);
	const membershipPlan = computeStopMembershipPlan(
		committedStops,
		finalRowKeysByShiftId,
		newStopsByKey,
		removedStopIds,
	);

	// ── Execute: shifts first (creates resolve the draft ids the stop creates need) ───────────
	const [createdShifts] = await Promise.all([
		input.creates.length > 0 ? deps.shiftsApi.createMany(input.creates) : Promise.resolve([] as Shift[]),
		updates.length > 0 ? deps.shiftsApi.updateMany(updates) : Promise.resolve([]),
	]);

	// Junction membership: creates carry their mixed order + the jobs accumulated before them
	// (MC sets `jobs` at create; Airtable mirrors it onto the jobs' Dropoffs/Shipping).
	const jobsBeforeKey = (list: RowKey[], targetKey: RowKey): string[] => {
		const acc: string[] = [];
		for (const key of list) {
			if (key === targetKey) return acc;
			const parsed = parseRowKey(key);
			if (parsed.kind === "job") acc.push(parsed.id);
			else acc.length = 0;
		}
		return acc;
	};
	const draftStopCreates = input.creates.flatMap((_, index) => {
		const list = createRowKeyLists[index];
		const shiftId = createdShifts[index]?.id;
		if (!shiftId) return [];
		return (createStopKeys.get(index) ?? []).flatMap((tempKey) => {
			const meta = newStopsByKey.get(tempKey);
			if (!meta) return [];
			return [
				{
					tempKey,
					stop: {
						dropoffId: meta.dropoffId,
						shiftId,
						order: newStopOrders.get(tempKey) ?? list.length,
						jobIds: jobsBeforeKey(list, newStopRowKey(tempKey)),
					},
				},
			];
		});
	});
	const confirmedStopCreates = membershipPlan.creates.map((create) => ({
		tempKey: create.tempKey,
		stop: {
			dropoffId: create.dropoffId,
			shiftId: create.shiftId,
			order: newStopOrders.get(create.tempKey) ?? 0,
			jobIds: jobsBeforeKey(finalRowKeysByShiftId.get(create.shiftId) ?? [], newStopRowKey(create.tempKey)),
		},
	}));
	const stopCreates = [...confirmedStopCreates, ...draftStopCreates];

	// Merge re-links and re-orders into one update per stop.
	const stopUpdateById = new Map<string, { id: string; shiftId?: string; order?: number }>();
	for (const relink of membershipPlan.relinks)
		stopUpdateById.set(relink.stopId, { id: relink.stopId, shiftId: relink.shiftId });
	for (const orderUpdate of stopOrders) {
		const existing = stopUpdateById.get(orderUpdate.stopId) ?? { id: orderUpdate.stopId };
		stopUpdateById.set(orderUpdate.stopId, { ...existing, order: orderUpdate.order });
	}

	const [createdStops] = await Promise.all([
		stopCreates.length > 0 ? deps.stopsApi.createMany(stopCreates.map((c) => c.stop)) : Promise.resolve([]),
		stopUpdateById.size > 0 ? deps.stopsApi.updateMany([...stopUpdateById.values()]) : Promise.resolve([]),
		membershipPlan.deletes.length > 0 ? deps.stopsApi.deleteMany(membershipPlan.deletes) : Promise.resolve(),
	]);

	// Order is a projection of each shift's mixed sequence; write after the links are in place.
	if (jobOrders.length > 0) await deps.jobsApi.setOrders(jobOrders);

	// ── Sample routing (MC's accumulator) over the RESOLVED sequences ──────────────────────────
	const createdIdByTempKey = new Map<string, string>();
	stopCreates.forEach((create, index) => {
		const created = createdStops[index];
		if (created?.id) createdIdByTempKey.set(create.tempKey, created.id);
	});
	const resolvedSequences = [...createRowKeyLists, ...finalRowKeysByShiftId.values()].map((list) => {
		const rows: { kind: "job" | "stop"; id: string }[] = [];
		for (const key of list) {
			const parsed = parseRowKey(key);
			if (parsed.kind === "job") rows.push({ kind: "job", id: parsed.id });
			else if (parsed.kind === "stop") rows.push({ kind: "stop", id: parsed.id });
			else {
				const created = createdIdByTempKey.get(parsed.id);
				if (created) rows.push({ kind: "stop", id: created });
			}
		}
		return rows;
	});
	const dropoffLinks = computeJobDropoffLinks(resolvedSequences, input.knownDropoffLinkByJobId ?? new Map());
	if (dropoffLinks.length > 0) await deps.jobsApi.setDropoffs(dropoffLinks);

	// ── Cluster pull-in points ──────────────────────────────────────────────────────────────────
	// Independent of the shift math: a point belongs to the JOB, not to the shift, so a job
	// unassigned after its point was staged still keeps the point. Minimal-write diff against the
	// known baseline, exactly like Order.
	const stagedLocations = input.stagedLocationByJobId ?? EMPTY_LOCATIONS;
	const knownLocations = input.knownLocationByJobId ?? EMPTY_KNOWN_LOCATIONS;
	const jobLocations: JobLocationUpdate[] = [];
	for (const [jobId, location] of stagedLocations) {
		if (knownLocations.get(jobId) === location) continue;
		jobLocations.push({ jobId, location });
	}
	if (jobLocations.length > 0) await deps.jobsApi.setLocations(jobLocations);

	// ── Pull-in zones (map features) ────────────────────────────────────────────────────────────
	// Deltas, not results: the endpoint reads each job's current column and merges, so a stale
	// board can never drop zones another dispatcher added. Like the point, a zone belongs to the
	// JOB, so it is independent of the shift math above.
	const mapFeatureEdits = buildMapFeatureEdits(
		input.stagedPullInZonesByJobId ?? EMPTY_ZONES,
		input.clearedPullInZoneJobIds ?? EMPTY_IDS,
	);
	if (mapFeatureEdits.length > 0) await deps.jobsApi.applyMapFeatureEdits(mapFeatureEdits);

	return { orderUpdates: jobOrders, locationUpdates: jobLocations, mapFeatureEdits };
}

export function makeShiftsUseCases(deps: ShiftsUseCasesDeps): ShiftsUseCases {
	return {
		saveScheduleChanges: (input) => saveScheduleChanges(deps, input),
	};
}
